// Teacher Leaderboard Service
// Handles all leaderboard-related API calls

import { supabase } from './supabaseClient';
import { getApiBaseUrl } from './config/apiConfig';

const API_BASE_URL = `${getApiBaseUrl()}/api`;

// Helper to get auth token
async function getAuthToken() {
  const { data: { session } } = await supabase.auth.getSession();
  return session?.access_token;
}

/**
 * Get current (live) teacher leaderboard
 * @param {string} type - 'monthly' or 'yearly'
 * @returns {Promise} { leaderboard[], period, type, updated_at }
 */
export async function getCurrentLeaderboard(type = 'monthly') {
  try {
    const response = await fetch(`${API_BASE_URL}/leaderboard/current?type=${type}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      const errorData = await response.json();
      return { data: null, error: errorData.error || 'Failed to fetch leaderboard' };
    }

    const data = await response.json();
    return { data, error: null };
  } catch (error) {
    console.error('Current leaderboard error:', error);
    return { data: null, error: error.message };
  }
}

/**
 * Get historical leaderboard (snapshot)
 * @param {string} period - Period key (e.g., '2025-01' for monthly, '2025' for yearly)
 * @param {string} type - 'monthly' or 'yearly'
 * @returns {Promise} { leaderboard[], period, type, snapshot_date }
 */
export async function getHistoricalLeaderboard(period, type = 'monthly') {
  try {
    const response = await fetch(
      `${API_BASE_URL}/leaderboard/history?period=${period}&type=${type}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      return { data: null, error: errorData.error || 'Failed to fetch historical leaderboard' };
    }

    const data = await response.json();
    return { data, error: null };
  } catch (error) {
    console.error('Historical leaderboard error:', error);
    return { data: null, error: error.message };
  }
}

/**
 * Get teacher's current rank
 * @param {string} teacherId - UUID of teacher
 * @param {string} type - 'monthly' or 'yearly'
 * @returns {Promise} { rank, total_teachers, teacher, badge, period }
 */
export async function getTeacherRank(teacherId, type = 'monthly') {
  try {
    const token = await getAuthToken();
    if (!token) {
      return { data: null, error: 'Not authenticated' };
    }

    const response = await fetch(
      `${API_BASE_URL}/leaderboard/teacher/${teacherId}?type=${type}`,
      {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      return { data: null, error: errorData.error || 'Failed to fetch teacher rank' };
    }

    const data = await response.json();
    return { data, error: null };
  } catch (error) {
    console.error('Teacher rank error:', error);
    return { data: null, error: error.message };
  }
}

/**
 * Get list of available historical periods
 * @param {string} type - 'monthly' or 'yearly'
 * @returns {Promise} { periods[], type }
 */
export async function getAvailablePeriods(type = 'monthly') {
  try {
    const response = await fetch(
      `${API_BASE_URL}/leaderboard/available-periods?type=${type}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      return { data: null, error: errorData.error || 'Failed to fetch periods' };
    }

    const data = await response.json();
    return { data, error: null };
  } catch (error) {
    console.error('Available periods error:', error);
    return { data: null, error: error.message };
  }
}
